/**
 * Resolves the affiliates (tenants) a user may act in from the Keycloak
 * `organization` claim carried by the OIDC id token.
 *
 * The claim is either a single organization (string) or a list of organizations
 * (string array) when the user belongs to several.
 */

/**
 * Every usable affiliate id in the claim, in claim order.
 *
 * A single string claim yields a one-entry list. Entries that are not usable
 * strings (non-strings, blank strings) are dropped rather than failing the whole
 * claim, so one malformed entry cannot lock a user out of the organizations they
 * legitimately belong to.
 *
 * @param organization The raw `organization` claim from the OIDC profile.
 */
export function resolveAffiliateIds(organization: unknown): readonly string[] {
  const candidates = Array.isArray(organization)
    ? organization
    : [organization];

  return candidates.flatMap((candidate) => {
    if (typeof candidate !== "string") {
      return [];
    }
    const trimmed = candidate.trim();
    return trimmed === "" ? [] : [trimmed];
  });
}

/**
 * Picks the effective affiliate id from the claim list and the user's stored
 * choice.
 *
 * The stored choice only wins while it is still in the claim: an organization
 * revoked between sessions would otherwise pin the app to a tenant every request
 * 403s on. In that case - and when nothing is stored - the last claim entry
 * wins, matching admin v1. An empty claim yields `null`, meaning "no tenant
 * selected".
 */
export function pickAffiliateId(
  availableIds: readonly string[],
  selectedId: string | null,
): string | null {
  if (selectedId !== null && availableIds.includes(selectedId)) {
    return selectedId;
  }
  return availableIds.at(-1) ?? null;
}
